/**
 * The date range chosen in the topbar.
 *
 * Lives in zustand rather than in the dashboard's own state because the control
 * that sets it (topbar) and the screen that reads it (dashboard) sit on opposite
 * sides of the layout — passing it through would mean threading a prop through
 * `AdminLayout` and `Outlet` for no benefit.
 *
 * Only the *intent* is persisted, so a reopened tab still means "last 7 days"
 * relative to today rather than to whenever the tab was last used.
 */

import { create } from 'zustand';
import { storageKeys } from '@/lib/env';
import {
  DATE_PRESETS,
  DEFAULT_DATE_FILTER,
  resolveDateFilter,
  type DateFilter,
  type DatePreset,
  type ResolvedRange,
} from '@/lib/dateFilter';

interface DateFilterState {
  filter: DateFilter;
  setPreset: (preset: DatePreset) => void;
  setCustom: (bounds: { from?: string; to?: string }) => void;
  reset: () => void;
}

function readStored(): DateFilter {
  try {
    const raw = localStorage.getItem(storageKeys.dateFilter);
    if (!raw) return DEFAULT_DATE_FILTER;
    const parsed = JSON.parse(raw) as DateFilter;
    if (!DATE_PRESETS.includes(parsed?.preset)) return DEFAULT_DATE_FILTER;
    return parsed;
  } catch {
    return DEFAULT_DATE_FILTER;
  }
}

function persist(filter: DateFilter) {
  try {
    localStorage.setItem(storageKeys.dateFilter, JSON.stringify(filter));
  } catch {
    // A full or blocked storage quota must never take the dashboard down.
  }
}

export const useDateFilterStore = create<DateFilterState>((set, get) => ({
  filter: readStored(),

  setPreset: (preset) => {
    const filter: DateFilter =
      preset === 'custom' ? { ...get().filter, preset } : { preset };
    persist(filter);
    set({ filter });
  },

  setCustom: (bounds) => {
    const filter: DateFilter = { ...get().filter, ...bounds, preset: 'custom' };
    persist(filter);
    set({ filter });
  },

  reset: () => {
    persist(DEFAULT_DATE_FILTER);
    set({ filter: DEFAULT_DATE_FILTER });
  },
}));

/**
 * The resolved range every dashboard query keys off.
 *
 * Recomputed per render on purpose — it is a handful of date-fns calls, and
 * memoising it against a moving "now" is how a dashboard ends up stuck on
 * yesterday's window. The result is a plain object of two strings, so react-query
 * still sees a stable key as long as the dates themselves have not moved.
 */
export function useResolvedRange(): ResolvedRange {
  const filter = useDateFilterStore((state) => state.filter);
  return resolveDateFilter(filter);
}
